/**
 * Sons d'UNE vidéo (lot 7) : volume de la voix, musique d'ambiance, jingles de
 * début et de fin et leur volume — choisis à la création de chaque vidéo, parmi la bibliothèque
 * « Sons des animations » (par défaut : les sons par défaut de la bibliothèque).
 * Une phase H.O.R.A peut avoir sa propre musique (sinon celle de l'animation) :
 * des morceaux courts, un par phase.
 */

import { useEffect, useRef, useState } from 'react';
import { ChevronDown, ExternalLink, Pause, Play, Upload } from 'lucide-react';
import { HORA_METHOD } from '@jeraisonne/db/cards';
import {
  animationAudioUrl,
  bundledJingleUrl,
  DEFAULT_GAIN,
  musicLabel,
  useAddAnimationAudio,
  useAnimationAudioList,
  type AnimationAudioKind,
  type AnimationAudioRow,
} from '@/lib/animations/audio';
import {
  VIDEO_HORA_PHASES,
  VOICE_GAIN_MAX,
  type VideoHoraPhase,
  type VideoSoundChoice,
  type VideoSoundLabels,
  type VideoSounds,
} from '@/lib/animations/video';

export interface SoundOption {
  value: VideoSoundChoice;
  label: string;
  /** à écouter ; `null` : sans son */
  url: string | null;
  gain: number;
}

const FIELD: Record<AnimationAudioKind, 'music' | 'jingleIntro' | 'jingleOutro'> = {
  music: 'music',
  jingle_intro: 'jingleIntro',
  jingle_outro: 'jingleOutro',
};

/** Volume choisi pour cette vidéo, par son (`null` : celui du son). */
const GAIN_FIELD: Record<AnimationAudioKind, 'musicGain' | 'jingleIntroGain' | 'jingleOutroGain'> =
  {
    music: 'musicGain',
    jingle_intro: 'jingleIntroGain',
    jingle_outro: 'jingleOutroGain',
  };

const TITLE: Record<AnimationAudioKind, string> = {
  music: 'Musique d’ambiance',
  jingle_intro: 'Jingle de début',
  jingle_outro: 'Jingle de fin',
};

/** Fichiers acceptés (FFmpeg les lit tous). */
export const AUDIO_ACCEPT =
  'audio/mpeg,audio/mp4,audio/aac,audio/ogg,audio/webm,.mp3,.m4a,.aac,.ogg,.webm';

/** Page de la bibliothèque des sons (écouter, renommer, volume, son par défaut). */
export const SOUNDS_PAGE = '/tutor/admin/animations/sons';

/** Volume de la musique : réglage de 0 à 40 % (elle reste sous la voix). */
export const MUSIC_GAIN_MAX = 0.4;

/** Volume maximal réglable, par son. */
const GAIN_MAX: Record<AnimationAudioKind, number> = {
  music: MUSIC_GAIN_MAX,
  jingle_intro: 1,
  jingle_outro: 1,
};

function GainSlider({
  id,
  label,
  name,
  value,
  max,
  step = 0.01,
  onChange,
  onReset,
  resetLabel = 'd’origine',
}: {
  id: string;
  label: string;
  /** nom lu (plusieurs curseurs « Volume » sur l'écran) */
  name: string;
  value: number;
  max: number;
  step?: number;
  onChange: (v: number) => void;
  /** affiché seulement si le volume a été changé */
  onReset: (() => void) | null;
  resetLabel?: string;
}) {
  return (
    <div className="mt-1.5 flex items-center gap-2">
      <label htmlFor={id} className="text-ink-600 shrink-0 text-xs">
        {label}
      </label>
      <input
        id={id}
        type="range"
        min={0}
        max={max}
        step={step}
        value={Math.min(max, value)}
        aria-label={name}
        onChange={(e) => onChange(Number(e.target.value))}
        className="accent-primary-500 min-h-11 min-w-0 flex-1"
      />
      <span className="text-ink-700 w-12 shrink-0 text-right text-xs tabular-nums">
        {Math.round(value * 100)} %
      </span>
      {onReset && (
        <button
          type="button"
          onClick={onReset}
          aria-label={`${name} : ${resetLabel}`}
          className="text-primary-700 min-h-11 shrink-0 text-xs font-semibold underline"
        >
          {resetLabel}
        </button>
      )}
    </div>
  );
}

/**
 * Choix possibles pour un son : par défaut (celui de la bibliothèque, sinon
 * le jingle JeRaisonne livré avec l'app), les autres sons de la bibliothèque,
 * le jingle livré, ou aucun. `rows` : `undefined` pendant le chargement.
 */
export function soundOptions(
  kind: AnimationAudioKind,
  rows: readonly AnimationAudioRow[] | undefined,
  urlOf: (path: string) => string = animationAudioUrl,
): SoundOption[] {
  const ofKind = (rows ?? []).filter((r) => r.kind === kind);
  const def = ofKind.find((r) => r.is_default) ?? null;
  const jingle = kind !== 'music';
  const bundled: SoundOption | null = jingle
    ? {
        value: 'bundled',
        label: 'Jingle JeRaisonne',
        url: bundledJingleUrl(kind),
        gain: DEFAULT_GAIN[kind],
      }
    : null;
  const out: SoundOption[] = [];
  if (def) {
    out.push({
      value: 'default',
      label: `Par défaut — ${def.label}`,
      url: urlOf(def.bucket_path),
      gain: def.gain,
    });
  } else if (!rows) {
    out.push({ value: 'default', label: 'Par défaut', url: null, gain: DEFAULT_GAIN[kind] });
  } else if (bundled) {
    out.push({ ...bundled, value: 'default', label: `Par défaut — ${bundled.label}` });
  } else {
    out.push({ value: 'default', label: 'Par défaut — sans musique', url: null, gain: 0 });
  }
  for (const r of ofKind) {
    if (r === def) continue;
    out.push({ value: r.id, label: r.label, url: urlOf(r.bucket_path), gain: r.gain });
  }
  // le jingle livré, s'il n'est pas déjà celui par défaut
  if (bundled && (def || !rows)) out.push(bundled);
  out.push({ value: 'none', label: jingle ? 'Sans jingle' : 'Sans musique', url: null, gain: 0 });
  return out;
}

/**
 * Choix de la musique d'UNE phase : celle de l'animation, une musique de la
 * bibliothèque, ou aucune. `rows` : `undefined` pendant le chargement.
 */
export function phaseMusicOptions(
  rows: readonly AnimationAudioRow[] | undefined,
  urlOf: (path: string) => string = animationAudioUrl,
): SoundOption[] {
  return [
    { value: 'default', label: 'Comme l’animation', url: null, gain: 0 },
    ...(rows ?? [])
      .filter((r) => r.kind === 'music')
      .map((r) => ({ value: r.id, label: r.label, url: urlOf(r.bucket_path), gain: r.gain })),
    { value: 'none', label: 'Sans musique', url: null, gain: 0 },
  ];
}

/** Nom d'une phase : « H — Hypothèse ». */
export function phaseName(p: VideoHoraPhase): string {
  const title = HORA_METHOD.find((m) => m.key === p)?.title;
  return title ? `${p} — ${title}` : p;
}

/** « Musique : X · début : Y · fin : Z » (sons d'une vidéo préparée). */
export function soundsSummary(l: VideoSoundLabels): string {
  const phases = l.musicPhases
    ? VIDEO_HORA_PHASES.filter((p) => l.musicPhases![p] !== undefined).map(
        (p) => `phase ${p} : ${l.musicPhases![p] ?? 'sans musique'}`,
      )
    : [];
  return [
    `Musique : ${l.music ?? 'aucune'}`,
    ...phases,
    `jingle de début : ${l.jingleIntro ?? 'aucun'}`,
    `jingle de fin : ${l.jingleOutro ?? 'aucun'}`,
  ].join(' · ');
}

const select =
  'border-ink-200 text-ink-800 focus:border-primary-300 min-h-11 w-full min-w-0 flex-1 rounded-xl border bg-white px-3 text-sm outline-none';

/** Son en cours d'écoute ou d'envoi : un type de son, ou la musique d'une phase. */
type SoundSlot = AnimationAudioKind | `phase-${VideoHoraPhase}`;

export function VideoSoundsPicker({
  value,
  onChange,
  disabled = false,
  phases = VIDEO_HORA_PHASES,
}: {
  value: VideoSounds;
  onChange: (next: VideoSounds) => void;
  disabled?: boolean;
  /** phases de l'animation (musique par phase) */
  phases?: readonly VideoHoraPhase[];
}) {
  const list = useAnimationAudioList();
  const add = useAddAnimationAudio();
  const [playing, setPlaying] = useState<SoundSlot | null>(null);
  const [uploading, setUploading] = useState<SoundSlot | null>(null);
  const audio = useRef<HTMLAudioElement | null>(null);
  const file = useRef<HTMLInputElement | null>(null);
  const uploadSlot = useRef<SoundSlot>('music');
  const phaseOverrides = phases.filter((p) => (value.musicPhases?.[p] ?? 'default') !== 'default');
  const [phasesOpen, setPhasesOpen] = useState(phaseOverrides.length > 0);

  useEffect(
    () => () => {
      audio.current?.pause();
    },
    [],
  );

  const options = {
    music: soundOptions('music', list.data),
    jingle_intro: soundOptions('jingle_intro', list.data),
    jingle_outro: soundOptions('jingle_outro', list.data),
  };
  // un son retiré de la bibliothèque depuis : retour au son par défaut
  const current = (kind: AnimationAudioKind): SoundOption => {
    const opts = options[kind];
    return opts.find((o) => o.value === value[FIELD[kind]]) ?? opts[0]!;
  };

  const phaseOptions = phaseMusicOptions(list.data);
  const currentPhase = (p: VideoHoraPhase): SoundOption =>
    phaseOptions.find((o) => o.value === (value.musicPhases?.[p] ?? 'default')) ?? phaseOptions[0]!;
  const urlOf = (slot: SoundSlot): string | null =>
    slot.startsWith('phase-')
      ? currentPhase(slot.slice(6) as VideoHoraPhase).url
      : current(slot as AnimationAudioKind).url;

  const stop = (slot: SoundSlot) => {
    if (playing === slot) {
      audio.current?.pause();
      setPlaying(null);
    }
  };

  const listen = (slot: SoundSlot) => {
    const el = audio.current ?? (audio.current = new Audio());
    if (playing === slot) {
      el.pause();
      setPlaying(null);
      return;
    }
    const url = urlOf(slot);
    if (!url) return;
    el.pause();
    el.src = url;
    el.currentTime = 0;
    el.onended = () => setPlaying(null);
    void el.play().then(
      () => setPlaying(slot),
      () => setPlaying(null),
    );
  };

  const setPhase = (p: VideoHoraPhase, choice: VideoSoundChoice) =>
    onChange({ ...value, musicPhases: { ...value.musicPhases, [p]: choice } });

  // un fichier de l'ordinateur : ajouté à la bibliothèque, puis choisi pour
  // cette vidéo (le son par défaut de la bibliothèque ne change pas)
  const upload = (slot: SoundSlot, f: File) => {
    const phase = slot.startsWith('phase-') ? (slot.slice(6) as VideoHoraPhase) : null;
    const kind: AnimationAudioKind = phase ? 'music' : (slot as AnimationAudioKind);
    setUploading(slot);
    add.mutate(
      {
        kind,
        blob: f,
        fileName: f.name,
        label: kind === 'music' ? musicLabel(f.name) : f.name.replace(/\.[a-z0-9]+$/i, ''),
      },
      {
        onSuccess: (row) =>
          phase
            ? setPhase(phase, row.id)
            : onChange({
                ...value,
                [FIELD[kind]]: row.id,
                [GAIN_FIELD[kind]]: null,
              }),
        onSettled: () => setUploading(null),
      },
    );
  };

  return (
    <fieldset className="space-y-2" data-testid="video-sounds" disabled={disabled}>
      <legend className="text-ink-600 mb-1 text-xs font-bold">Sons de cette vidéo</legend>
      <input
        ref={file}
        type="file"
        accept={AUDIO_ACCEPT}
        className="sr-only"
        tabIndex={-1}
        aria-hidden
        data-testid="video-sound-file"
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          if (f) upload(uploadSlot.current, f);
        }}
      />
      <div>
        <GainSlider
          id="video-voice-gain"
          label="Voix"
          name="Volume de la voix"
          value={value.voiceGain}
          max={VOICE_GAIN_MAX}
          step={0.05}
          onChange={(g) => onChange({ ...value, voiceGain: g })}
          onReset={
            Math.abs(value.voiceGain - 1) > 0.005
              ? () => onChange({ ...value, voiceGain: 1 })
              : null
          }
          resetLabel="100 %"
        />
        <p className="text-ink-500 text-[12px]">
          Voix trop faible ? Monte-la : la musique et les jingles restent au même niveau, puis tout
          le son est égalisé.
        </p>
      </div>
      {(['music', 'jingle_intro', 'jingle_outro'] as const).map((kind) => {
        const opt = current(kind);
        return (
          <div key={kind}>
            <label htmlFor={`video-sound-${kind}`} className="text-ink-600 mb-0.5 block text-xs">
              {TITLE[kind]}
            </label>
            <div className="flex items-center gap-2">
              <select
                id={`video-sound-${kind}`}
                className={select}
                value={opt.value}
                onChange={(e) => {
                  stop(kind);
                  onChange({
                    ...value,
                    [FIELD[kind]]: e.target.value,
                    // autre son : son propre volume
                    [GAIN_FIELD[kind]]: null,
                  });
                }}
              >
                {options[kind].map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <button
                type="button"
                disabled={uploading !== null}
                onClick={() => {
                  uploadSlot.current = kind;
                  file.current?.click();
                }}
                aria-label={`Ajouter un fichier : ${TITLE[kind].toLowerCase()}`}
                title="Ajouter un fichier de ton ordinateur"
                className="text-ink-700 hover:bg-ink-50 border-ink-200 flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border disabled:opacity-40"
              >
                <Upload
                  className={uploading === kind ? 'h-4 w-4 animate-pulse' : 'h-4 w-4'}
                  aria-hidden
                />
              </button>
              <button
                type="button"
                disabled={!opt.url}
                onClick={() => listen(kind)}
                aria-label={`${playing === kind ? 'Arrêter' : 'Écouter'} : ${TITLE[kind].toLowerCase()}`}
                className="text-ink-700 hover:bg-ink-50 border-ink-200 flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border disabled:opacity-40"
              >
                {playing === kind ? (
                  <Pause className="h-4 w-4" aria-hidden />
                ) : (
                  <Play className="h-4 w-4" aria-hidden />
                )}
              </button>
            </div>
            {opt.url && (
              <GainSlider
                id={`video-sound-gain-${kind}`}
                label="Volume"
                name={`Volume : ${TITLE[kind].toLowerCase()}`}
                value={value[GAIN_FIELD[kind]] ?? opt.gain}
                max={GAIN_MAX[kind]}
                onChange={(g) => onChange({ ...value, [GAIN_FIELD[kind]]: g })}
                onReset={
                  value[GAIN_FIELD[kind]] !== null
                    ? () => onChange({ ...value, [GAIN_FIELD[kind]]: null })
                    : null
                }
              />
            )}
            {kind === 'music' && phases.length > 0 && (
              <div className="border-ink-100 mt-2 rounded-xl border p-2">
                <button
                  type="button"
                  aria-expanded={phasesOpen}
                  aria-controls="video-music-phases"
                  onClick={() => setPhasesOpen((o) => !o)}
                  className="text-ink-700 flex min-h-11 w-full items-center justify-between gap-2 text-left text-xs font-semibold"
                >
                  <span>
                    Musique par phase H.O.R.A
                    {phaseOverrides.length > 0
                      ? ` · ${phaseOverrides.length} phase${phaseOverrides.length > 1 ? 's' : ''} changée${phaseOverrides.length > 1 ? 's' : ''}`
                      : ''}
                  </span>
                  <ChevronDown
                    className={`h-4 w-4 shrink-0 transition-transform ${phasesOpen ? 'rotate-180' : ''}`}
                    aria-hidden
                  />
                </button>
                {phasesOpen && (
                  <div id="video-music-phases" className="space-y-2">
                    <p className="text-ink-500 text-[12px]">
                      Par défaut, la musique d’ambiance joue sur toute l’animation. Une phase peut
                      avoir la sienne : des morceaux plus courts, faciles à trouver libres de
                      droits. Même volume que la musique d’ambiance.
                    </p>
                    {phases.map((p) => {
                      const slot = `phase-${p}` as const;
                      const opt = currentPhase(p);
                      return (
                        <div key={p}>
                          <label
                            htmlFor={`video-music-phase-${p}`}
                            className="text-ink-600 mb-0.5 block text-xs"
                          >
                            {phaseName(p)}
                          </label>
                          <div className="flex items-center gap-2">
                            <select
                              id={`video-music-phase-${p}`}
                              className={select}
                              value={opt.value}
                              onChange={(e) => {
                                stop(slot);
                                setPhase(p, e.target.value);
                              }}
                            >
                              {phaseOptions.map((o) => (
                                <option key={o.value} value={o.value}>
                                  {o.label}
                                </option>
                              ))}
                            </select>
                            <button
                              type="button"
                              disabled={uploading !== null}
                              onClick={() => {
                                uploadSlot.current = slot;
                                file.current?.click();
                              }}
                              aria-label={`Ajouter un fichier : musique de la phase ${p}`}
                              title="Ajouter un fichier de ton ordinateur"
                              className="text-ink-700 hover:bg-ink-50 border-ink-200 flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border disabled:opacity-40"
                            >
                              <Upload
                                className={uploading === slot ? 'h-4 w-4 animate-pulse' : 'h-4 w-4'}
                                aria-hidden
                              />
                            </button>
                            <button
                              type="button"
                              disabled={!opt.url}
                              onClick={() => listen(slot)}
                              aria-label={`${playing === slot ? 'Arrêter' : 'Écouter'} : musique de la phase ${p}`}
                              className="text-ink-700 hover:bg-ink-50 border-ink-200 flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border disabled:opacity-40"
                            >
                              {playing === slot ? (
                                <Pause className="h-4 w-4" aria-hidden />
                              ) : (
                                <Play className="h-4 w-4" aria-hidden />
                              )}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
      {uploading && (
        <p role="status" className="text-ink-600 text-[12px]">
          Envoi du fichier…
        </p>
      )}
      {add.error && (
        <p role="alert" className="text-error-600 text-[13px]">
          {add.error.message}
        </p>
      )}
      <p className="text-ink-500 text-[12px]">
        Sons choisis pour cette vidéo seulement. Les volumes, eux, valent aussi pour l’animation
        dans l’app et les prochaines vidéos (enregistrés au lancement). Un fichier ajouté ici
        rejoint la bibliothèque.{' '}
        <a
          href={SOUNDS_PAGE}
          target="_blank"
          rel="noreferrer"
          className="text-primary-700 inline-flex items-center gap-0.5 font-semibold underline"
        >
          Gérer les sons
          <ExternalLink className="h-3 w-3" aria-hidden />
        </a>
      </p>
    </fieldset>
  );
}
